import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Container } from 'reactstrap';
import { connect } from 'react-redux';
import { useQuery } from 'react-apollo';
import { get, flatten, cloneDeep } from 'lodash';
import GET_THEMATICS_OVERVIEW_DATA from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/getThematicsOverviewData';
import { updateRoute } from '_Resources/Header/Actions/updateRoute';
import { getRoute } from '_Utils/routes/routeUtils';
import CategoryHierarchyCard from '_Cards/ThematicsCard/ThematicsOverviewCards/CategoryHierarchyCard/CategoryHierarchyCard';
import CategoryRankingCard from '_Cards/ThematicsCard/ThematicsOverviewCards/CategoryRankingCard/CategoryRankingCard';
import EmotionsTriggersCard from '_Cards/ThematicsCard/ThematicsOverviewCards/EmotionsTriggersCard/EmotionsTriggersCard';
import DataByThematics from '_Cards/ThematicsCard/ThematicsOverviewCards/DataByThematics/DataByThematics';
import CategoryBreakdownCard from '_Cards/ThematicsCard/ThematicsOverviewCards/CategoryBreakdownCard/CategoryBreakdownCard';
import { HintsContent } from '_Components/QcardPanel/QcardHeader/QcardHints/Hint';
import styled from 'styled-components';
import {
  getDefaultSettings,
  getFlattenedData,
  formatDeep,
} from '_Cards/ThematicsCard/ThematicsOverviewCards/CategoryBreakdownCard/utils';
import ThematicsHorizontalChart from '_Cards/ThematicsCard/ThematicsOverviewCards/ThematicsHorizontalChartCard/ThematicsHorizontalChart';

const StyledContainer = styled(Container)`
  height: 52rem;
`;

const ThematicsOverview = ({
  idpage,
  dispatchUpdateRoute,
  currentTab,
  filterKeys,
  isDetailsToggled,
  message,
  currentGraphView,
  exportLink,
  toTab,
  selectedThematicSelectionOption,
  projectId,
  range,
}) => {
  const [data, setData] = useState([]);
  const [flattenedData, setFlattenedData] = useState([]);
  const [settings, setSettings] = useState({});
  const [allCategoriesHierarchy, setAllCategoriesHierarchy] = useState([]);
  const [queryFilters, setQueryFilters] = useState({});

  const { data: thematicsOverviewData, loading, refetch } = useQuery(GET_THEMATICS_OVERVIEW_DATA, {
    variables: {
      ...queryFilters,
      projectId,
      range,
      thematicSelectionId: selectedThematicSelectionOption?.id || null,
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    getRoute(dispatchUpdateRoute, idpage);
  }, []);

  useEffect(() => {
    const filters = cloneDeep(filterKeys);
    filters.keywords = flatten(filterKeys.texts?.filter(t => t.name === 'keywords').map(t => t.values)) || [];
    delete filters.texts;
    setQueryFilters(filters);
  }, [filterKeys]);

  let formattedData = [];

  const formatDeep = (arrayToIterate, level, objToFill) => {
    let newObjToFill = {};
    arrayToIterate?.map(item => {
      const childrenKey = Object.keys(item).find(i => Array.isArray(item[i]));
      newObjToFill = {
        ...objToFill,
        [level]: item.thematicsOverviewDetails.name,
      };
      formattedData = [...formattedData, newObjToFill];
      if (Array.isArray(item[childrenKey])) {
        formatDeep(item[childrenKey], childrenKey?.toLowerCase()?.substring(0, childrenKey.length - 1), newObjToFill);
      }
    });
    return formattedData;
  };

  const categoriesHierarchies = formatDeep(
    thematicsOverviewData?.getThematicsOverviewData?.thematics,
    'thematic',
    {},
  ).map(item => {
    if (!item.subthematic) {
      return { ...item, subthematic: null, subsubthematic: null };
    }
    if (!item.subsubthematic) {
      return { ...item, subsubthematic: null };
    }
    return item;
  });

  const defaultSettings = getDefaultSettings(categoriesHierarchies);

  useEffect(() => {
    if (thematicsOverviewData) {
      setData(thematicsOverviewData.getThematicsOverviewData.thematics);
      setFlattenedData(getFlattenedData(thematicsOverviewData.getThematicsOverviewData.thematics));
      setAllCategoriesHierarchy(categoriesHierarchies);

      setSettings(
        JSON.parse(thematicsOverviewData.getThematicsOverviewData.settings)
          ? {
              ...defaultSettings,
              ...JSON.parse(thematicsOverviewData.getThematicsOverviewData.settings),
              selectedThematics:
                JSON.parse(thematicsOverviewData.getThematicsOverviewData.settings).selectedThematics || formattedData,
            }
          : defaultSettings,
      );
    }
  }, [thematicsOverviewData]);

  const getGraphView = () => {
    let graphView;
    switch (currentGraphView) {
      case 'category-breakdown':
        graphView = (
          <CategoryBreakdownCard
            idpage={idpage}
            data={flattenedData}
            settings={settings}
            setSettings={setSettings}
            rawData={data}
            verbatimsWithoutThematicCount={
              thematicsOverviewData?.getThematicsOverviewData?.verbatimsWithoutThematicCount
            }
            queryFilters={queryFilters}
            loading={loading}
          />
        );
        break;
      case 'emotions-trigger':
        graphView = (
          <EmotionsTriggersCard
            data={flattenedData}
            settings={settings}
            setSettings={setSettings}
            isDetailsToggled={isDetailsToggled}
            message={message}
            queryFilters={queryFilters}
          />
        );
        break;
      case 'data-by-thematics':
        graphView = (
          <DataByThematics data={flattenedData} settings={settings} setSettings={setSettings} refetch={refetch} />
        );
        break;
      case 'horizontal-thematics':
        graphView = (
          <>
            <ThematicsHorizontalChart
              data={flattenedData}
              settings={settings}
              setSettings={setSettings}
              refetch={refetch}
            />
          </>
        );
        break;
      default:
        graphView = (
          <DataByThematics data={flattenedData} settings={settings} setSettings={setSettings} refetch={refetch} />
        );
    }
    return graphView;
  };

  const getBody = () => {
    let body;
    switch (currentTab) {
      case 'category-hierarchy':
        body = (
          <CategoryHierarchyCard
            thematics={data}
            settings={settings}
            projectId={projectId}
            range={range}
            filterKeys={queryFilters}
            allSelectedCategories={allCategoriesHierarchy}
            hints={[
              HintsContent.HINT_EINDEX_BADGE,
              HintsContent.HINT_SPEECH_ENGAGEMENT,
              HintsContent.HINT_EMOTIONAL_INTENSITY,
              HintsContent.HINT_AI_ACTIONS,
            ]}
            message={message}
            currentTab={currentTab}
          />
        );
        break;
      case 'category-ranking':
        body = (
          <CategoryRankingCard
            filteredData={flattenedData}
            rawData={data}
            isDetailsToggled={isDetailsToggled}
            settings={settings}
            projectId={projectId}
            filterKeys={queryFilters}
            setSettings={setSettings}
            hints={[
              HintsContent.HINT_EINDEX_BADGE,
              HintsContent.HINT_SPEECH_ENGAGEMENT,
              HintsContent.HINT_EMOTIONAL_INTENSITY,
            ]}
            message={message}
            exportLink={exportLink}
            toTab={toTab}
          />
        );
        break;
      case 'graph-view':
        body = getGraphView();
        break;
      default:
        body = (
          <CategoryHierarchyCard
            thematics={data}
            settings={settings}
            projectId={projectId}
            range={range}
            filterKeys={queryFilters}
            allSelectedCategories={allCategoriesHierarchy}
            hints={[
              HintsContent.HINT_EINDEX_BADGE,
              HintsContent.HINT_SPEECH_ENGAGEMENT,
              HintsContent.HINT_EMOTIONAL_INTENSITY,
            ]}
            message={message}
            currentTab={currentTab}
          />
        );
    }
    return body;
  };

  return (
    <StyledContainer fluid className="py-3 px-4">
      {getBody()}
    </StyledContainer>
  );
};

ThematicsOverview.propTypes = {
  idpage: PropTypes.string,
  dispatchUpdateRoute: PropTypes.func.isRequired,
  currentTab: PropTypes.string.isRequired,
  exportLink: PropTypes.bool,
  toTab: PropTypes.number,
  filterKeys: PropTypes.shape({
    criteriaKeys: PropTypes.arrayOf(PropTypes.string),
    dataCollectorId: PropTypes.number,
    projectId: PropTypes.number,
    range: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string,
    }).isRequired,
    texts: PropTypes.string,
    thematics: PropTypes.arrayOf(PropTypes.string),
  }),
  isDetailsToggled: PropTypes.bool,
  message: PropTypes.string,
  currentGraphView: PropTypes.string,
  selectedThematicSelectionOption: PropTypes.shape({
    createdBy: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }),
    id: PropTypes.number,
    label: PropTypes.string,
    name: PropTypes.string,
    projectId: PropTypes.number,
    thematics: PropTypes.arrayOf(
      PropTypes.shape({
        thematic: PropTypes.string.isRequired,
        subThematic: PropTypes.string || null,
        subSubThematic: PropTypes.string || null,
      }),
    ),
  }),
  projectId: PropTypes.number,
  range: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
  }),
};

ThematicsOverview.defaultProps = {
  idpage: 'thematics-overview',
  exportLink: false,
  toTab: 1,
  filterKeys: {},
  isDetailsToggled: false,
  message: '',
  currentGraphView: 'category-breakdown',
  selectedThematicSelectionOption: {
    createdBy: {
      firstName: '',
      lastName: '',
      email: '',
    },
    id: 0,
    label: '',
    name: '',
    projectId: 0,
    thematics: {
      thematic: '',
      subThematic: null,
      subSubThematic: null,
    },
  },
  projectId: 0,
  range: {
    startDate: '2023-01-01T00:00:00',
    endDate: '2023-12-31T23:59:59',
  },
};

function mapStateToProps(state) {
  return {
    filterKeys: get(state, ['filters', 'filterKeys']),
    selectedThematicSelectionOption: get(state, ['thematicSelections', 'selectedThematicSelectionOption']),
    projectId: get(state, ['projects', 'currentProject', 'id']),
    range: get(state, ['periods', 'currentPeriod']),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatchUpdateRoute: newState => {
      dispatch(updateRoute(newState));
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ThematicsOverview);
